import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Typography,CircularProgress  } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useAuth } from "../../hooks/AuthProvider";
import { Download } from "@mui/icons-material";
import {
  BUYERS_EXPORT_ENDPOINT,
  BUYERS_LIST_ENDPOINT,
} from "../../config/urls";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";

const columns = [
  { id: "name", label: "Name", align: "left" },
  { id: "phone_no", label: "Phone Number", align: "left" },
  { id: "address", label: "Address", align: "left" },
  { id: "total_orders_placed", label: "Total Orders Placed", align: "left" },
  { id: "total_spent", label: "Total Spent", align: "left" },
  {
    id: "last_successful_order",
    label: "Last Successful Order",
    align: "left",
  },
];

export default function Buyers({ refreshKey, onNavigate }) {
  const { getValidToken } = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [showExportInfo, setShowExportInfo] = useState(false);
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  const [buyers, setBuyers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  const fetchData = async () => {
    let url = BUYERS_LIST_ENDPOINT;

    // Construct query parameters for server-side filtering and pagination
    let queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search: searchQuery,
    });

    url += `?${queryParams}`;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      setBuyers(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [controller.page, controller.rowsPerPage, refreshKey, searchQuery]);

  // pagination
  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const updateSearchValue = (value) => {
    if (value.length > 50) {
      return;
    }
    setSearchQuery(value);
    setCharCount(value.length);
    setController((prev) => ({
      ...prev,
      page: 0,
    }));
  };

  const handleExport = async () => {
    if (rowCount > 5000) {
      const confirmed = window.confirm(
        `You are exporting ${rowCount} buyers.\nThis may take some time.\nDo you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    setExportLoading(true);
    setShowExportInfo(true);
    let url = BUYERS_EXPORT_ENDPOINT;

    // Construct query parameters for server-side filtering and pagination
    let queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search: searchQuery,
    });

    url += `?${queryParams}`;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        try {
          const error = (await response.json()).error;
          alert(error);
          throw new Error(error);
        } catch {
          throw new Error("Failed to export data");
        }
      }

      // Handle CSV response - clone response for blob processing
      const responseClone = response.clone();
      const blob = await responseClone.blob();

      // Create a link element to download the file
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;

      // Use the current date for the filename
      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];
      a.download = `buyers-${dateStr}.csv`;

      // Append to the document and trigger download
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(urlBlob);
      document.body.removeChild(a);
      return;
    } catch (error) {
  console.error("Error exporting data:", error.message);
  throw error;
} finally {
  setExportLoading(false);
  setShowExportInfo(false);
}
  };

  // Define table columns for AppTable
  const tableColumns = columns.map((column) => ({
    id: column.id,
    label: column.label,
    align: column.align,
    render: (row) => {
      if (column.id === "name") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.name || "N/A"}
          </Typography>
        );
      }

      if (column.id === "phone_no") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.phone_display || "N/A"}
          </Typography>
        );
      }

      if (column.id === "address") {
        return (
          <Typography
            sx={{
              ...typography.bodyLarge,
              maxWidth: 250,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "#1D2330",
            }}
          >
            {row.address || "N/A"}
          </Typography>
        );
      }

      if (column.id === "total_orders_placed") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.total_orders_placed || 0}
          </Typography>
        );
      }

      if (column.id === "total_spent") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.total_spend ? `₹${row.total_spend}` : "N/A"}
          </Typography>
        );
      }

      if (column.id === "last_successful_order") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.last_successful_order || "N/A"}
          </Typography>
        );
      }

      return <Typography sx={typography.bodyLarge}>N/A</Typography>;
    },
  }));

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC",
    },
    "& td": {
      borderBottom: "none",
      borderTop: "none",
    },
  });

  return (
    <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
      {/* Header Section with Search and Export Button */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Search - Takes remaining width */}
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => updateSearchValue(e.target.value)}
            charCount={charCount}
            tooltip="Search by Name or Phone Number"
            placeholder="Search by name or phone number"
          />
        </Box>

        {/* Export Button - Fixed width */}
        <Box sx={{ flex: "0 0 auto" }}>
          <ButtonOne
            variant="contained"
            disabled={exportLoading}
            onClick={handleExport}
            startIcon={
  exportLoading ? (
    <CircularProgress size={18} color="inherit" />
  ) : (
    <Download sx={{ fontSize: 18 }} />
  )
}
            sx={{
              ...typography.bodyLarge,
              backgroundColor: ColorsConstants.primaryPurple100,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: ColorsConstants.primaryPurple100,
                opacity: 0.9,
              },
            }}
          >
           {exportLoading ? "Exporting..." : "Export"}
          </ButtonOne>
        </Box>
      </Box>

      {/* Export progress feedback */}
      <Snackbar
        open={showExportInfo}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" variant="filled" sx={{ width: "100%" }}>
          Preparing export...
          <br />
          Large exports may take some time.
        </Alert>
      </Snackbar>

      {/* Table using AppTable */}
      <AppTable
        columns={tableColumns}
        rows={buyers}
        rowKey={(row, index) => index}
        maxHeight={540}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F3F6FB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C5CEDB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#A8B4C7",
          },
        }}
        rowSx={rowSx}
        paperSx={{
          border: "1px solid #DCDFE5",
          borderRadius: "12px",
          mb: 2,
        }}
        emptyMessage="No buyers found"
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}